import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import type { FileProps } from "models/Base";

export type FileStatus = "queued" | "uploading" | "completed" | "error" | "onProcess" | "success";

export class FileProgress {
  id: string;
  file: File; // <-- composition, not inheritance
  progress: number;
  status: FileStatus;
  categories: string;
  visible_to: string[];
  cropped: boolean;
  fileSize: number;
  fileType: string;
  preview?: string;

  constructor(file: File, isCropped = false) {
    this.id = uuidv4();
    this.file = file;
    this.progress = 0;
    this.status = "onProcess";
    this.categories = "";
    this.visible_to = [];
    this.cropped = isCropped;
    this.fileSize = file.size;
    this.fileType = this.extractFileType(file.name);
    try {
      if (file && file.type?.startsWith("image/") && file instanceof Blob) {
        this.preview = URL.createObjectURL(file);
      }
    } catch (error) {
      this.preview = undefined;
    }
  }

  // convenient getters so the rest of your UI can still read name/size/type
  get name() {
    return this.file.name;
  }
  get size() {
    return this.file.size;
  }
  get type() {
    return this.file.type;
  }
  get lastModified() {
    return this.file.lastModified;
  }

  private extractFileType(filename: string): string {
    const m = filename.match(/\.([^.]+)$/);
    return m?.[1]?.toLowerCase() ?? "unknown";
  }

  // setters / helpers for your custom fields
  setProgress(v: number) {
    this.progress = v;
  }
  setStatus(v: FileStatus) {
    this.status = v;
  }
  setCategories(v: string) {
    this.categories = v;
  }
  setVisibleTo(v: string[]) {
    this.visible_to = v;
  }
  setCropped(v: boolean) {
    this.cropped = v;
  }
  setFileSize(v: number) {
    this.fileSize = v;
  }
  setFileType(v: string) {
    this.fileType = v;
  }
}

interface FileUploadContextType {
  files: FileProgress[];
  addFile: (file: File, isCropped?: boolean) => FileProgress;
  emptyFiles: () => void;
  setFileProgress: (id: string, progress: number) => void;
  setFileStatus: (id: string, status: FileStatus) => void;
  updateFileCategory: (id: string, categories: string) => void;
  updateFileVisibility: (id: string, visible_to: string[]) => void;
  updateFile: <T extends FileProgress>(id: string, patch: Partial<T>) => void;
  replaceFile: (id: string, newFile: File) => void;
  removeFile: (id: string) => void;
}

const FileUploadContext = createContext<FileUploadContextType | undefined>(undefined);

export const useUpload = () => {
  const ctx = useContext(FileUploadContext);
  if (!ctx) throw new Error("useUpload must be used within an UploadProvider");
  return ctx;
};

export const FileUploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [files, setFiles] = useState<FileProgress[]>([]);

  const addFile = useCallback((file: File, isCropped = false): FileProgress => {
    const fp = new FileProgress(file, isCropped);
    setFiles((prev) => [...prev, fp]);
    return fp;
  }, []);

  const emptyFiles = useCallback(() => setFiles([]), []);

  const setFileProgress = useCallback((id: string, progress: number) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? cloneWith(f, { progress }) : f)));
  }, []);

  const setFileStatus = useCallback((id: string, status: FileStatus) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? cloneWith(f, { status }) : f)));
  }, []);

  const updateFileCategory = useCallback((id: string, categories: string) => {
    setFiles((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const next = cloneWith(f, { categories });
        try {
          (next.file as FileProps).categories = categories;
        } catch {
          // ignore — file might be immutable in some environments
        }
        return next;
      })
    );
  }, []);

  const updateFileVisibility = useCallback((id: string, visible_to: string[]) => {
    setFiles((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const next = cloneWith(f, { visible_to });
        try {
          (next.file as FileProps).visible_to = visible_to;
        } catch {
          // ignore
        }
        return next;
      })
    );
  }, []);

  // generic safe updater (no DOM read-only props here because we don't extend File)
  const updateFile = useCallback(<T extends FileProgress>(id: string, patch: Partial<T>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? cloneWith<T>(f as T, patch) : f)));
  }, []);

  // swap underlying bytes/file object (used after cropping)
  const replaceFile = useCallback((id: string, newFile: File) => {
    setFiles((prev) =>
      prev.map((f) => {
        if (f.id !== id) return f;
        const next = new FileProgress(newFile, f.cropped);
        // preserve identity & custom fields
        next.id = f.id;
        next.progress = f.progress;
        next.status = f.status;
        next.categories = f.categories;
        next.visible_to = f.visible_to;
        try {
          (next.file as FileProps).categories = next.categories;
          (next.file as FileProps).visible_to = next.visible_to;
        } catch {
          // ignore mutation failures
        }
        // keep/refresh preview
        if (f.preview && f.preview !== next.preview) {
          // optional: revoke old URL to avoid leaks
          URL.revokeObjectURL(f.preview);
        }
        next.preview = newFile.type?.startsWith("image/")
          ? URL.createObjectURL(newFile)
          : f.preview;
        return next;
      })
    );
  }, []);

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const value = useMemo(
    () => ({
      files,
      addFile,
      emptyFiles,
      setFileProgress,
      setFileStatus,
      updateFileCategory,
      updateFileVisibility,
      updateFile,
      replaceFile,
      removeFile
    }),
    [
      files,
      addFile,
      emptyFiles,
      setFileProgress,
      setFileStatus,
      updateFileCategory,
      updateFileVisibility,
      updateFile,
      replaceFile,
      removeFile
    ]
  );

  return <FileUploadContext.Provider value={value}>{children}</FileUploadContext.Provider>;
};

// helper: preserve class prototype (not DOM prototypes) when patching
function cloneWith<T extends object>(obj: T, patch: Partial<T>): T {
  const clone = Object.assign(Object.create(Object.getPrototypeOf(obj)), obj);
  return Object.assign(clone, patch);
}
